import React, { useEffect, createElement } from 'react';
interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
}
const SITE_NAME = 'HBS Impact';
const SITE_URL = 'https://hbsimpact.com';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/u58yyudAVGYZM45T7aPn3Z/PP_Logo_Lime.svg';
const LOCALE = 'de_DE';
function setMeta(name: string, content: string, attribute = 'name') {
  let el = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement | null;
  if (el) {
    el.content = content;
  } else {
    el = document.createElement('meta');
    el.setAttribute(attribute, name);
    el.content = content;
    document.head.appendChild(el);
  }
}
function setCanonical(url: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (el) {
    el.href = url;
  } else {
    el = document.createElement('link');
    el.rel = 'canonical';
    el.href = url;
    document.head.appendChild(el);
  }
}
function setJsonLd(data: Record<string, unknown>, id: string) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (el) {
    el.textContent = JSON.stringify(data);
  } else {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
  }
}
export function SEO({
  title,
  description,
  canonical,
  type = 'website',
  noindex = false,
  jsonLd
}: SEOProps) {
  useEffect(() => {
    // Document title
    document.title = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    // Language
    document.documentElement.lang = 'de';
    // Basic meta
    setMeta('description', description);
    setMeta('author', 'HBS Impact – Philip Michael Steinke');
    setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large');
    // Open Graph
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', type, 'property');
    setMeta('og:locale', LOCALE, 'property');
    setMeta('og:site_name', SITE_NAME, 'property');
    setMeta('og:image', LOGO_URL, 'property');
    if (canonical) {
      setMeta('og:url', canonical, 'property');
    }
    // Twitter Card
    setMeta('twitter:card', 'summary');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', LOGO_URL);
    // Canonical
    if (canonical) {
      setCanonical(canonical);
    }
    // Custom JSON-LD
    if (jsonLd) {
      setJsonLd(jsonLd, 'page-jsonld');
    }
    // Organization JSON-LD (always present)
    setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'HBS Impact',
      legalName: 'PP Immobilien & Managment GmbH',
      url: SITE_URL,
      logo: LOGO_URL,
      description: 'Strategische Unternehmensberatung für Geschäftsführer und Gesellschafter. Spezialisiert auf GmbH-Gründung, Holding-Strukturierung, Geschäftsführer-Beratung und Nachfolgeplanung in Berlin.',
      foundingLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Grüner Weg 59A',
          postalCode: '12359',
          addressLocality: 'Berlin',
          addressCountry: 'DE'
        }
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+49-151-14796710',
        email: 'info@hbsimpact.com',
        contactType: 'customer service',
        availableLanguage: ['German', 'English']
      },
      sameAs: [],
      knowsAbout: ['GmbH-Gründung', 'Holding-Strukturierung', 'Geschäftsführer-Beratung', 'Nachfolgeplanung', 'Gesellschaftsrecht', 'Unternehmensberatung', 'Beteiligungsmodelle', 'Haftungsmanagement', 'Vergütungsoptimierung', 'Steueroptimierung'],
      areaServed: {
        '@type': 'Country',
        name: 'Germany'
      }
    }, 'org-jsonld');
  }, [title, description, canonical, type, noindex, jsonLd]);
  return null;
}